"use client";

import { useMemo } from 'react'
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from 'recharts'
import { useFinance } from '@/lib/finance/finance-data-context'
import { fmtPct, fmtUsd, fmtUsdCompact } from '@/lib/finance/format'
import { Card, LegendToggle, Money, StatTile, useToggleSet } from '@/components/finance/ui'
import { ChartTooltip, crosshair } from '@/components/finance/ChartTooltip'
import { xAxisProps, yAxisProps, gridProps, CHART_MARGIN, ACTIVE_DOT } from '@/components/finance/chartDefaults'

/**
 * Projection tooltip — the shared readout, but with the null-valued actual
 * series dropped (actuals only exist through the current year, so future years
 * would otherwise list blank "(actual)" rows). recharts injects
 * active/payload/label; labelFormatter is supplied by the caller.
 */
function ProjectionTooltip(props: {
  active?: boolean
  payload?: { name?: string; value?: number | string; color?: string; dataKey?: string }[]
  label?: string | number
  labelFormatter?: (label: string) => string
}) {
  return <ChartTooltip {...props} payload={props.payload?.filter((p) => p.value != null && p.value !== '')} />
}

/**
 * FIRE net worth is the page's headline — the only series shown to start, and
 * the one the actual-vs-projection callout tracks. The rest are toggleable
 * overlays, off by default. Colors follow the app-wide series mapping so a
 * metric reads the same hue here as on every other chart.
 */
const FIRE_KEY = 'fireNetWorth'
const SERIES = [
  { key: FIRE_KEY, label: 'FIRE net worth', color: 'var(--series-5)' },
  { key: 'netWorth', label: 'Net worth', color: 'var(--series-1)' },
  { key: 'investments', label: 'Investments', color: 'var(--series-2)' },
  { key: 'retirement', label: 'Retirement', color: 'var(--series-3)' },
  { key: 'cash', label: 'Cash + equivalents', color: 'var(--series-4)' },
  { key: 'debts', label: 'Debts', color: 'var(--series-6)' },
] as const

export function Projections() {
  const { projection, networth } = useFinance()
  const [enabled, toggle] = useToggleSet<string>([FIRE_KEY])

  const rows = useMemo(() => {
    const byYear = new Map<number, Record<string, number | string | null>>()
    for (const s of SERIES) {
      for (const p of projection[s.key] ?? []) {
        let row = byYear.get(p.year)
        if (!row) {
          row = { year: p.year, label: p.label }
          byYear.set(p.year, row)
        }
        row[s.key] = p.value
        // Keep the most descriptive label ("FI (2032)" over "2032").
        if (p.label.length > String(row.label).length) row.label = p.label
      }
    }
    return [...byYear.values()].sort((a, b) => (a.year as number) - (b.year as number))
  }, [projection])

  // Actual value per calendar year, pulled straight from the app's real net-worth
  // series (the same numbers the Net Worth page charts). Balances are date-keyed
  // and ascending, so the last snapshot within a year wins as that year's actual.
  const actualByYear = useMemo(() => {
    const out: Record<string, Record<number, number>> = {}
    for (const s of SERIES) {
      const series = networth.series[s.key] ?? {}
      const perYear: Record<number, number> = {}
      for (const m of networth.months) {
        const v = series[m]
        if (v == null) continue
        perYear[Number(m.slice(0, 4))] = v
      }
      out[s.key] = perYear
    }
    return out
  }, [networth])

  // Merge the actual points onto each projection row (`<key>Act`). Only years
  // that already have real data get a value — future years stay blank.
  const chartRows = useMemo(
    () =>
      rows.map((r) => {
        const merged: Record<string, number | string | null> = { ...r }
        for (const s of SERIES) {
          const a = actualByYear[s.key]?.[r.year as number]
          if (a != null) merged[`${s.key}Act`] = a
        }
        return merged
      }),
    [rows, actualByYear],
  )

  const milestones = useMemo(
    () =>
      rows
        .filter((r) => /[A-Za-z]/.test(String(r.label)))
        .map((r) => ({ year: r.year as number, label: String(r.label), netWorth: r.netWorth as number | null })),
    [rows],
  )

  // Headline comparison: FIRE actual vs projection for the most recent year we
  // have real data for. This is the "how are we tracking?" number.
  const fireCompare = useMemo(() => {
    const years = rows
      .map((r) => r.year as number)
      .filter((y) => actualByYear[FIRE_KEY]?.[y] != null)
    if (!years.length) return null
    const year = Math.max(...years)
    const actual = actualByYear[FIRE_KEY][year]
    const projected = rows.find((r) => r.year === year)?.[FIRE_KEY]
    if (typeof projected !== 'number') return null
    return { year, actual, projected, delta: actual - projected }
  }, [rows, actualByYear])

  const active = SERIES.filter((s) => enabled.has(s.key))

  const compareNode = fireCompare ? (
    <div className="text-right">
      <div className="text-xs text-ink-2">FIRE · actual vs projection ({fireCompare.year})</div>
      <div className="mt-0.5 text-sm">
        <span className="font-semibold tabular-nums">{fmtUsdCompact(fireCompare.actual)}</span>
        <span className="text-ink-3"> vs {fmtUsdCompact(fireCompare.projected)} projected</span>{' '}
        <Money value={fireCompare.delta} compact />{' '}
        <span className="text-ink-3">
          ({fireCompare.delta >= 0 ? '+' : ''}
          {fmtPct(fireCompare.delta / fireCompare.projected)})
        </span>
      </div>
    </div>
  ) : undefined

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Projections</h1>
          <p className="mt-0.5 text-xs text-ink-3">
            From the PROJECTION model in the Financial Accounts sheet
          </p>
        </div>
      </div>

      {milestones.length > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {milestones.map((m) => (
            <StatTile
              key={m.label}
              label={m.label}
              value={m.netWorth != null ? fmtUsd(m.netWorth) : '—'}
            />
          ))}
        </div>
      )}

      <Card title="Projected balances by year" actions={compareNode}>
        <div className="mb-1 flex flex-wrap gap-1.5">
          {SERIES.map((s) => (
            <LegendToggle
              key={s.key}
              label={s.label}
              color={s.color}
              shape="line"
              hidden={!enabled.has(s.key)}
              onToggle={() => toggle(s.key)}
            />
          ))}
        </div>
        <p className="mb-2 text-xs text-ink-3">Solid = projection · dashed = actual</p>

        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows} margin={CHART_MARGIN}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="year" {...xAxisProps} />
              <YAxis {...yAxisProps} domain={['auto', 'auto']} width={60} />
              <Tooltip
                cursor={crosshair}
                content={
                  <ProjectionTooltip
                    labelFormatter={(y) =>
                      String(rows.find((r) => String(r.year) === y)?.label ?? y)
                    }
                  />
                }
              />
              <ReferenceLine y={0} stroke="var(--baseline)" />
              {milestones.map((m) => (
                <ReferenceLine
                  key={m.label}
                  x={m.year}
                  stroke="var(--baseline)"
                  label={{
                    value: m.label,
                    position: 'top',
                    fill: 'var(--text-muted)',
                    fontSize: 10,
                  }}
                />
              ))}
              {active.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={ACTIVE_DOT}
                  connectNulls
                />
              ))}
              {active.map((s) => (
                <Line
                  key={`${s.key}Act`}
                  type="monotone"
                  dataKey={`${s.key}Act`}
                  name={`${s.label} (actual)`}
                  stroke={s.color}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={{ r: 3, fill: s.color, stroke: 'var(--surface-1)', strokeWidth: 1 }}
                  activeDot={ACTIVE_DOT}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  )
}
